import useFetch from "../../../../hooks/useFetch";
import { API } from "../../../../Services/api-services";

// Real responses are wrapped as { success, data }; hooks below unwrap so
// consumers (mappers) only ever see the data payload.
const unwrapEnvelope = (result) => result?.data ?? null;

/**
 * Fire-and-forget progress heartbeat — used only for VideoPane's final send
 * on unmount. Bypasses useFetch (its mutate() sets state on the calling
 * component, which has already unmounted by the time the request resolves).
 * POST /mentorLms/mentor/contents/:contentId/progress
 * @param {number|string} contentId
 * @param {{ positionSeconds: number, watchedPercent: number }} payload
 */
export const sendVideoProgressBeacon = (contentId, payload) =>
  API.post(`/mentorLms/mentor/contents/${contentId}/progress`, payload).catch(() => {});

/**
 * Fetch the mentor's assigned course catalog with tab counts.
 * GET /mentorLms/mentor/courses
 * Returns: { counts: { all, inProgress, notStarted, completed },
 *   courses: [{ courseId, title, thumbnailUrl, videoCount, progressPercent, status, isPriorityWatch }] }
 * Server-sorted: priority first, then inProgress → notStarted → completed.
 * @param {Object} options
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useFetchMyCourses = (options = {}) => {
  const real = useFetch("/mentorLms/mentor/courses", { method: "get", ...options });
  return { ...real, apiData: unwrapEnvelope(real.apiData) };
};

/**
 * Nav badge count (req 6): how many assigned videos this mentor hasn't
 * watched yet, across every published course (locked ones included — total
 * outstanding, not just what's currently clickable).
 * GET /mentorLms/mentor/unwatchedCount
 * Returns: { unwatchedCount: number }
 * @param {Object} options
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useFetchUnwatchedCount = (options = {}) => {
  const real = useFetch("/mentorLms/mentor/unwatchedCount", { method: "get", ...options });
  return { ...real, apiData: unwrapEnvelope(real.apiData) };
};

/**
 * Fetch one assigned course's player state (ordered contents + lock/progress).
 * GET /mentorLms/mentor/courses/:courseId
 * Returns: { courseId, title, progressPercent, currentContentId,
 *   contents: [{ contentId, position, videoTitle, videoSource, videoUrl, videoDurationSeconds,
 *     isUnlocked, videoCompleted, videoState, assessment, state }] }
 * @param {number|string} courseId
 * @param {Object} options
 * @returns {{ apiData, loading, error, refetch, mutate }}
 */
export const useFetchMyCourse = (courseId, options = {}) => {
  const real = useFetch(`/mentorLms/mentor/courses/${courseId}`, {
    method: "get",
    ...options,
    enabled: !!courseId && (options.enabled ?? true),
  });
  return { ...real, apiData: unwrapEnvelope(real.apiData) };
};

/**
 * Mark the current content's video as watched (no body). Server-gated: 403 if locked.
 * POST /mentorLms/mentor/contents/:contentId/complete
 * Returns: { contentId, videoCompleted, assessmentAvailable, assessmentId,
 *   nextContentId, progressPercent, status }
 * Fire via `completeVideo(contentId)`.
 * @param {Object} options
 * @returns {{ completeVideo, apiData, loading, error }}
 */
export const useCompleteVideo = (options = {}) => {
  const { mutate, ...rest } = useFetch("", { method: "post", manual: true, ...options });
  const completeVideo = async (contentId, opts = {}) =>
    unwrapEnvelope(await mutate({ url: `/mentorLms/mentor/contents/${contentId}/complete`, ...opts }));
  return { completeVideo, ...rest };
};

/**
 * Resume-position / watch% heartbeat (req 7). Fire via
 * `saveProgress(contentId, { positionSeconds, watchedPercent })` on a client
 * throttle during playback — not on every tick. Server-gated the same as
 * complete (403 if locked/expired); never touches completion state, so it's
 * safe to keep firing during a rewatch (req 2).
 * POST /mentorLms/mentor/contents/:contentId/progress
 * Returns: { contentId, watchedPercent, lastWatchedPositionSeconds }
 * @param {Object} options
 * @returns {{ saveProgress, apiData, loading, error }}
 */
export const useSaveVideoProgress = (options = {}) => {
  const { mutate, ...rest } = useFetch("", { method: "post", manual: true, ...options });
  const saveProgress = async (contentId, payload, opts = {}) =>
    unwrapEnvelope(await mutate({ url: `/mentorLms/mentor/contents/${contentId}/progress`, data: payload, ...opts }));
  return { saveProgress, ...rest };
};

/**
 * Fetch the quiz questions to take (correct answers are never included).
 * GET /mentorLms/mentor/assessments/:assessmentId
 * Returns: { assessmentId, title, totalQuestions, passScore, attemptsUsed,
 *   questions: [{ questionId, position, questionText, options: [{ optionNumber, text }] }] }
 * Gated with { success: 0, message }: 403 content locked / video not complete,
 * 400 already passed. Attempts are unlimited, so there is no "no attempts left" gate.
 * Fire via `fetchAssessment(assessmentId)`.
 * @param {Object} options
 * @returns {{ fetchAssessment, apiData, loading, error }}
 */
export const useFetchAssessment = (options = {}) => {
  const { mutate, ...rest } = useFetch("", { method: "get", manual: true, ...options });
  const fetchAssessment = async (assessmentId, opts = {}) =>
    unwrapEnvelope(await mutate({ url: `/mentorLms/mentor/assessments/${assessmentId}`, ...opts }));
  return { fetchAssessment, ...rest };
};

/**
 * Submit one full graded attempt. `answers` maps questionId → optionNumber (1-based).
 * POST /mentorLms/mentor/assessments/:assessmentId/attempts
 * Returns: { passed, score, total, passScore, attemptsUsed, mustRewatch, nextContentId }
 * Attempts are unlimited — every attempt is recorded for analytics, but there is
 * no cap and no hard lock, so no `attemptsRemaining`/`locked` field exists.
 * Fire via `submitAssessment(assessmentId, { answers })`.
 * @param {Object} options
 * @returns {{ submitAssessment, apiData, loading, error }}
 */
export const useSubmitAssessment = (options = {}) => {
  const { mutate, ...rest } = useFetch("", { method: "post", manual: true, ...options });
  const submitAssessment = async (assessmentId, payload, opts = {}) =>
    unwrapEnvelope(
      await mutate({
        url: `/mentorLms/mentor/assessments/${assessmentId}/attempts`,
        data: payload,
        ...opts,
      }),
    );
  return { submitAssessment, ...rest };
};
